/**
 * Editor Events — shared shapes for editor panel synchronization.
 *
 * Only events that are **dispatched or consumed by shared editor code** live
 * here (behavior changes fired from the undo commands, resource changes
 * fired from create/delete paths). Sandbox-specific events like the clip
 * editor belong in `trace-sandbox/src/editor-events.ts`; TraceFrontend
 * -specific ones should live alongside TraceFrontend's viewer provider.
 *
 * NOTE: event detail uses a string `objectPath` rather than a SceneObject ref
 * because these events cross popup window boundaries (via `window.opener`),
 * where only structured-cloneable payloads survive. Command/public APIs use
 * refs; the event layer is an explicit exception.
 */

/**
 * Only events that are dispatched OR consumed by shared editor code belong
 * here. Consumer-specific event constants (e.g. sandbox's clip editor, or
 * TraceFrontend-only UI events) should live in their own consumer's event
 * module and be namespaced distinctly from `editor:` if they aren't part of
 * this shared contract.
 */
export const EDITOR_EVENTS = {
    BEHAVIOR_CHANGED: 'editor:behavior-changed',
    RESOURCES_CHANGED: 'editor:resources-changed',
} as const;

export interface BehaviorChangedDetail {
    /** Fully qualified transform path (FQTP) of the changed object. */
    objectPath: string;
    behaviorType: string;
    action: 'added' | 'updated' | 'removed';
}

/**
 * Dispatch an editor event to this window, and to the opener window when present
 * (popup → opener). Delivery is DEFERRED to a microtask so listeners run AFTER the
 * triggering commit's call stack has unwound — not synchronously mid-commit. A
 * synchronous dispatch let a listener (e.g. a behaviors panel's refreshSceneData →
 * captureTransform) observe and re-serialize a half-applied commit's live state:
 * notably it could read a gizmo-moved live node as "authored" and bake the move
 * into Base. The microtask defer closes that whole class of windows structurally
 * and removes the need for self-reentrancy flags at the listener sites.
 *
 * Each target window is deferred in its OWN microtask: microtasks don't cross
 * realms, so the opener defers within its own realm too. The opener is captured
 * NOW (not re-read inside the microtask) so the event always reaches the window
 * that was the opener at call time, even if `window.opener` is replaced in the
 * sub-millisecond gap before the microtask runs. `detail` is captured per dispatch;
 * callers must not mutate it afterward (they don't — these details are fresh literals).
 */
function dispatchEditorEvent(eventName: string, detail: BehaviorChangedDetail | ResourcesChangedDetail): void {
    // CustomEvent does not copy detail, and both microtasks (this window + opener)
    // share the same reference. Freeze so a same-realm listener can't mutate the
    // payload the opener later sees. Callers pass fresh literals, so this is making
    // the contract structural, not fixing an active bug.
    Object.freeze(detail);

    queueMicrotask(() => {
        window.dispatchEvent(new CustomEvent(eventName, { detail }));
    });

    const opener = window.opener as Window | null;
    if (opener) {
        queueMicrotask(() => {
            try {
                opener.dispatchEvent(new CustomEvent(eventName, { detail }));
            } catch (_e) {
                // Cross-realm dispatch into the opener can throw if it has been
                // closed/navigated (SecurityError) — or if an opener-side listener
                // itself throws. Either way we swallow: a notification to another
                // window must not break this window's commit. Local-window listener
                // errors are NOT caught here (they surface normally above).
            }
        });
    }
}

/**
 * Dispatch a behavior-changed event to notify other panels.
 * Call this after adding, updating, or removing behaviors.
 * Deferred + opener-forwarded; see {@link dispatchEditorEvent}.
 */
export function dispatchBehaviorChanged(detail: BehaviorChangedDetail): void {
    dispatchEditorEvent(EDITOR_EVENTS.BEHAVIOR_CHANGED, detail);
}

export interface ResourcesChangedDetail {
    action: 'added' | 'removed' | 'cleared' | 'loaded';
    /** Asset path that changed (null for bulk actions like 'cleared' / 'loaded') */
    path: string | null;
}

/**
 * Dispatch a resources-changed event to notify the Resources panel.
 * Deferred + opener-forwarded; see {@link dispatchEditorEvent}.
 */
export function dispatchResourcesChanged(detail: ResourcesChangedDetail): void {
    dispatchEditorEvent(EDITOR_EVENTS.RESOURCES_CHANGED, detail);
}
